/**
 * Organizers list component.
 * Displays and manages event organizers (ministries and/or accounts).
 */

import { useState } from "react";
import {
  IconPlus,
  IconTrash,
  IconUsers,
  IconCheck,
} from "../../../../components/Icons.tsx";
import { haptic, safeShowAlert, safeShowConfirm } from "../../../../lib/telegram.ts";
import { getErrorMessage } from "../../../../lib/graphql.ts";
import {
  type EventOrganizer,
  type OrganizerRole,
  ORGANIZER_ROLE_CONFIG,
} from "../types.ts";
import { MinistriesPicker } from "./MinistriesPicker.tsx";
import { useAddOrganizer, useRemoveOrganizer } from "../hooks";

interface OrganizersListProps {
  eventId: string;
  organizers: EventOrganizer[];
  onUpdate: () => void;
}

// Add organizer form
function AddOrganizerForm({
  eventId,
  onAdd,
  onCancel,
}: {
  eventId: string;
  onAdd: () => void;
  onCancel: () => void;
}) {
  const [selectedMinistryIds, setSelectedMinistryIds] = useState<string[]>([]);
  const [role, setRole] = useState<OrganizerRole>("ORGANIZER");

  const { addOrganizer, isLoading: isAdding } = useAddOrganizer();

  const handleAdd = async () => {
    if (selectedMinistryIds.length === 0) {
      safeShowAlert("Выберите служение");
      return;
    }

    try {
      await addOrganizer(eventId, {
        ministryId: selectedMinistryIds[0],
        role,
        canEdit: role === "OWNER" || role === "ORGANIZER",
        canManageRsvp: role === "OWNER" || role === "ORGANIZER",
        canManageTickets: role === "OWNER",
        receiveNotifications: true,
      });
      haptic("success");
      onAdd();
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка добавления"));
    }
  };

  return (
    <div className="bg-slate-50 rounded-xl p-4 space-y-4">
      <h4 className="font-medium text-slate-900 text-[14px]">
        Добавить организатора
      </h4>

      <div>
        <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
          Служение
        </label>
        <MinistriesPicker
          selectedIds={selectedMinistryIds}
          onChange={setSelectedMinistryIds}
          placeholder="Выберите служение..."
          single
        />
      </div>

      <div>
        <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
          Роль
        </label>
        <div className="flex gap-2">
          {(Object.keys(ORGANIZER_ROLE_CONFIG) as OrganizerRole[]).map(
            (r) => {
              const config = ORGANIZER_ROLE_CONFIG[r];
              const isSelected = r === role;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => {
                    haptic("light");
                    setRole(r);
                  }}
                  className={`flex-1 py-2 px-3 rounded-lg text-[13px] font-medium transition-colors ${
                    isSelected
                      ? "bg-[var(--color-accent)] text-white"
                      : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {config.label}
                </button>
              );
            },
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 py-2 px-3 bg-white border border-slate-200 rounded-lg text-[13px] font-medium text-slate-600 hover:bg-slate-50 transition-colors"
        >
          Отмена
        </button>
        <button
          type="button"
          onClick={handleAdd}
          disabled={isAdding || selectedMinistryIds.length === 0}
          className="flex-1 py-2 px-3 bg-[var(--color-accent)] rounded-lg text-[13px] font-medium text-white hover:bg-[var(--color-accent-hover)] transition-colors disabled:opacity-50"
        >
          {isAdding ? "Добавление..." : "Добавить"}
        </button>
      </div>
    </div>
  );
}

// Organizer card
function OrganizerCard({
  organizer,
  onRemove,
}: {
  organizer: EventOrganizer;
  onRemove: () => void;
}) {
  const { removeOrganizer, isLoading: isRemoving } = useRemoveOrganizer();

  const handleRemove = async () => {
    const confirmed = await new Promise<boolean>((resolve) =>
      safeShowConfirm("Удалить организатора?", resolve),
    );
    if (!confirmed) return;

    try {
      await removeOrganizer(organizer.id);
      haptic("success");
      onRemove();
    } catch (err) {
      haptic("error");
      safeShowAlert(getErrorMessage(err, "Ошибка удаления"));
    }
  };

  // Get display name
  const displayName = organizer.ministry
    ? organizer.ministry.name
    : organizer.account
      ? [organizer.account.firstName, organizer.account.lastName]
          .filter(Boolean)
          .join(" ") || organizer.account.username || "Пользователь"
      : "Неизвестно";

  const roleConfig = ORGANIZER_ROLE_CONFIG[organizer.role];

  return (
    <div className="flex items-center gap-3 p-3 bg-white rounded-xl border border-slate-100">
      {/* Icon */}
      <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 flex-shrink-0">
        <IconUsers className="w-5 h-5" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-900 text-[14px] truncate">
            {displayName}
          </span>
          <span className="px-2 py-0.5 bg-slate-100 rounded text-[11px] font-medium text-slate-600 flex-shrink-0">
            {roleConfig.label}
          </span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-1">
          {organizer.canEdit && (
            <span className="text-[11px] text-slate-500">
              <IconCheck className="w-3 h-3 inline mr-0.5" />
              Редактирование
            </span>
          )}
          {organizer.canManageRsvp && (
            <span className="text-[11px] text-slate-500">
              <IconCheck className="w-3 h-3 inline mr-0.5" />
              RSVP
            </span>
          )}
          {organizer.canManageTickets && (
            <span className="text-[11px] text-slate-500">
              <IconCheck className="w-3 h-3 inline mr-0.5" />
              Билеты
            </span>
          )}
        </div>
      </div>

      {/* Remove button */}
      <button
        type="button"
        onClick={handleRemove}
        disabled={isRemoving}
        className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
      >
        <IconTrash className="w-4 h-4" />
      </button>
    </div>
  );
}

export function OrganizersList({
  eventId,
  organizers,
  onUpdate,
}: OrganizersListProps) {
  const [isAdding, setIsAdding] = useState(false);

  return (
    <div className="space-y-3">
      {/* Add button */}
      {!isAdding && (
        <button
          type="button"
          onClick={() => {
            haptic("light");
            setIsAdding(true);
          }}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-slate-200 rounded-xl text-[var(--color-accent)] font-medium hover:border-[var(--color-accent)] transition-colors"
        >
          <IconPlus className="w-5 h-5" />
          Добавить организатора
        </button>
      )}

      {/* Add form */}
      {isAdding && (
        <AddOrganizerForm
          eventId={eventId}
          onAdd={() => {
            setIsAdding(false);
            onUpdate();
          }}
          onCancel={() => setIsAdding(false)}
        />
      )}

      {/* Organizers list */}
      {organizers.length > 0 ? (
        <div className="space-y-2">
          {organizers.map((org) => (
            <OrganizerCard
              key={org.id}
              organizer={org}
              onRemove={onUpdate}
            />
          ))}
        </div>
      ) : (
        !isAdding && (
          <p className="text-center text-slate-500 text-[13px] py-4">
            Нет организаторов
          </p>
        )
      )}
    </div>
  );
}
